var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
import { jsxs, jsx } from "react/jsx-runtime";
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useFormikContext, Formik } from "formik";
import { useBoolean } from "@fluentui/react-hooks";
import { AsyncRequestStatus } from "@ane/core/lib/models";
import logger from "@ane/core/lib/services/logging/Logger";
import "../../MessageBar/index.js";
import "../../Spinner/index.js";
import "../../FormActions/index.js";
import "../../Button/index.js";
import "../../Stack.js";
import "../../FormHeading.js";
import "../../Loading.js";
import { EntityManagementGroupTelemetryEventNames } from "../../../telemetry/EntityManagementGroupTelemetryEventNames.js";
import "../../NoContentMessage/index.js";
import { CreateEntityBaseDismissReason } from "./CreateEntityBase.types.js";
import { SpinnerSize } from "@fluentui/react/lib/Spinner";
import { FormHeading } from "../../FormHeading/FormHeading.js";
import { LoadingInformation } from "../../Loading/Loading.js";
import { MessageBarType } from "@fluentui/react/lib/MessageBar";
import { Spinner } from "../../Spinner/Spinner.js";
import { Stack } from "../../Stack/Stack.js";
import { NoContentMessage } from "../../NoContentMessage/NoContentMessage.js";
import { FormActions } from "../../FormActions/FormActions.js";
import { PrimaryButton } from "../../Button/PrimaryButton.js";
import { SecondaryButton } from "../../Button/SecondaryButton.js";
const defaultSpinnerProps = {
  ariaLabel: "loading",
  size: SpinnerSize.large,
  label: "Loading...",
  labelPosition: "bottom",
  style: { height: "100%" }
};
const CreateEntityBaseComponent = (props) => {
  const {
    formId,
    response,
    dataTelemetryId,
    isReadonly = false,
    formHeadingStyles,
    renderFormHeader,
    renderFormFields,
    onInitialize,
    mapResponseToErrors,
    isOpen: propsIsOpen,
    onCancel: propsOnCancel,
    onDismiss: propsOnDismiss,
    onDismissed: propsOnDismissed,
    entityManagementGroupFieldName,
    onRenderSaveButton,
    onRenderBaseComponent
  } = props;
  const { isValid, dirty, isSubmitting, submitCount, resetForm, setErrors, submitForm } = useFormikContext();
  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(propsIsOpen);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initializingError, setInitializingError] = useState();
  const [dismissReason, setDismissReason] = useState();
  const isSaving = useMemo(() => {
    return submitCount > 0 && (isSubmitting || response.status === AsyncRequestStatus.Pending);
  }, [submitCount, isSubmitting, response]);
  const didMountRef = useRef(false);
  const cancelForm = () => {
    propsOnCancel && propsOnCancel();
    dismissForm(CreateEntityBaseDismissReason.Cancel);
  };
  const dismissForm = useCallback(
    (reason) => {
      setDismissReason(reason);
      propsOnDismiss && propsOnDismiss(reason);
      dismissPanel();
      resetForm();
    },
    [dismissPanel, setDismissReason, resetForm, propsOnDismiss]
  );
  const onDismissed = useCallback(() => {
    if (!dismissReason) {
      return;
    }
    propsOnDismissed && propsOnDismissed(dismissReason);
  }, [dismissReason, propsOnDismissed]);
  const renderHeader = () => /* @__PURE__ */ jsxs(FormHeading, { styles: formHeadingStyles, children: [
    renderFormHeader && renderFormHeader(),
    submitCount > 0 && response.status === AsyncRequestStatus.Failed && /* @__PURE__ */ jsx(
      LoadingInformation,
      {
        heading: "Error",
        message: response.error,
        messageBarProps: {
          ariaLabel: "error",
          messageBarType: MessageBarType.error
        }
      }
    )
  ] });
  const renderSpinner = (additionalProps = {}) => /* @__PURE__ */ jsx(
    Spinner,
    __spreadProps(__spreadValues({}, __spreadValues(__spreadValues({}, defaultSpinnerProps), additionalProps)), {
      styles: {
        root: { position: "absolute", left: 0, right: 0, top: 0, bottom: 0 }
      }
    })
  );
  const renderInitializingError = () => /* @__PURE__ */ jsx(
    Stack,
    {
      styles: {
        root: { position: "absolute", left: 0, right: 0, top: 0, bottom: 0 }
      },
      children: /* @__PURE__ */ jsx(
        NoContentMessage,
        {
          iconName: "SomethingWentWrong",
          heading: "Something went wrong",
          description: JSON.stringify(initializingError)
        }
      )
    }
  );
  const renderContent = () => {
    if (initializingError) {
      return renderInitializingError();
    } else if (isInitializing) {
      return renderSpinner();
    }
    return renderFormFields();
  };
  const [saveBtnClicked, setSaveBtnClicked] = useState(false);
  const renderFooter = () => /* @__PURE__ */ jsx(Stack, { horizontal: true, horizontalAlign: "start", verticalAlign: "center", children: /* @__PURE__ */ jsxs(FormActions, { children: [
    !!onRenderSaveButton ? onRenderSaveButton({
      disabled: !(isValid && dirty) || isSaving || isReadonly,
      formId,
      onClick: () => {
        setSaveBtnClicked(true);
        submitForm();
      },
      dataTelemetryId: !!dataTelemetryId ? `${dataTelemetryId}_save` : void 0
    }) : /* @__PURE__ */ jsx(
      PrimaryButton,
      {
        disabled: !(isValid && dirty) || isSaving || isReadonly,
        type: "submit",
        ariaLabel: "Save and close",
        text: "Save and close",
        form: formId,
        onClick: () => setSaveBtnClicked(true),
        "data-telemetry-id": !!dataTelemetryId ? `${dataTelemetryId}_save` : void 0
      }
    ),
    /* @__PURE__ */ jsx(
      SecondaryButton,
      {
        ariaLabel: "Cancel",
        text: "Cancel",
        onClick: cancelForm,
        "data-telemetry-id": !!dataTelemetryId ? `${dataTelemetryId}_cancel` : void 0
      }
    ),
    isSaving && /* @__PURE__ */ jsx(Spinner, { size: SpinnerSize.small, labelPosition: "right", label: "Saving...", ariaLabel: "saving" })
  ] }) });
  useEffect(() => {
    if (didMountRef.current === false) {
      didMountRef.current = true;
      return;
    }
    propsIsOpen ? openPanel() : dismissForm(CreateEntityBaseDismissReason.CallerProp);
  }, [propsIsOpen, openPanel, dismissForm]);
  useEffect(() => {
    if (!onInitialize || !setIsInitializing || !isOpen) {
      return;
    }
    let isDisposed = false;
    setIsInitializing(true);
    (() => __async(void 0, null, function* () {
      try {
        yield Promise.resolve(onInitialize());
      } catch (error) {
        setInitializingError(error);
      } finally {
        if (!isDisposed) {
          setIsInitializing(false);
        }
      }
    }))();
    return () => {
      isDisposed = true;
    };
  }, [setIsInitializing, onInitialize, isOpen]);
  useEffect(() => {
    if (isSaving || isInitializing || !saveBtnClicked) {
      return;
    }
    if (response.status === AsyncRequestStatus.Completed) {
      dismissForm(CreateEntityBaseDismissReason.Success);
    }
    setSaveBtnClicked(false);
  }, [response, isSaving, isInitializing, dismissForm]);
  useEffect(() => {
    if (!mapResponseToErrors || !setErrors || submitCount <= 0 || response.status !== AsyncRequestStatus.Failed) {
      return;
    }
    setErrors(mapResponseToErrors(response));
  }, [response, mapResponseToErrors, setErrors, submitCount]);
  useEffect(() => {
    return () => {
      if (!!entityManagementGroupFieldName) {
        logger.setEntityManagementGroup("");
        logger.trackEvent({
          measurements: {},
          name: EntityManagementGroupTelemetryEventNames.Entity_Management_Group_Empty,
          properties: {}
        });
      }
    };
  }, []);
  return onRenderBaseComponent({
    isOpen,
    renderHeader,
    renderFooter,
    cancelForm,
    onDismissed,
    renderContent
  });
};
const CreateEntityBase = (props) => {
  const { entityManagementGroupFieldName } = props;
  const updateEntityManagementGroup = (values) => {
    if (!!entityManagementGroupFieldName) {
      logger.setEntityManagementGroup(values[entityManagementGroupFieldName]);
      logger.trackEvent({
        measurements: {},
        name: EntityManagementGroupTelemetryEventNames.Entity_Management_Group_From_FormField,
        properties: {}
      });
    }
  };
  const formikConfig = __spreadProps(__spreadValues({}, props.formikConfig), {
    onSubmit: (values, formikHelpers) => {
      updateEntityManagementGroup(values);
      props.formikConfig.onSubmit(values, formikHelpers);
    }
  });
  return (
    // @ts-ignore ts 4.x
    /* @__PURE__ */ jsx(Formik, __spreadProps(__spreadValues({}, formikConfig), { children: /* @__PURE__ */ jsx(CreateEntityBaseComponent, __spreadValues({}, props)) }))
  );
};
export {
  CreateEntityBase
};
//# sourceMappingURL=CreateEntityBase.js.map
